// =============================================================================
// src/features/explore/explore-vote-layer.ts — Overlay Leaflet dos modos de voto.
//
// Delegado do AmazonasExploreMap (mantem explore-map.ts < 500 linhas). Desenha, no
//   centroide de cada municipio (reusa munCentroids — NAO recalcula):
//   - bolhas (L.marker + L.divIcon HTML): cor do PARTIDO, raio escalado em "absolutos"
//     e fixo em "percentual"/"ranking", rotulo branco. O numero final ja entra no HTML
//     (default visivel headless); o count-up e enhancement progressivo.
//   - campo de calor (L.circleMarker translucido): raio/opacidade por peso, matiz do
//     partido — circulos sobrepostos formam a densidade (nativo, sem leaflet.heat).
//
// SRP: so a camada Leaflet do overlay. Derivacao/cor vem de explore-vote-data;
//   formatacao/animacao de explore-vote-anim. Identidade visual: ./explore-vote.css.
// =============================================================================

import L from 'leaflet';
import type { Map as LeafletMap, LatLng, LayerGroup } from 'leaflet';
import type { ElectoralYear } from '../../app/types';
import type { ExploreVoteViz } from './explore-vote-constants';
import {
  BUBBLE_BASE_RADIUS,
  BUBBLE_STROKE,
  BUBBLE_STROKE_WIDTH,
  BUBBLE_FILL_OPACITY,
} from './explore-vote-constants';
import {
  deriveVoteRows,
  deriveZoneVoteRows,
  voteScaleMax,
  raioDaBolha,
  bubbleColorOf,
  heatWeightOf,
  heatColorFor,
  heatRadiusFor,
  heatOpacityFor,
  type VoteRow,
  type VoteCandidateSel,
} from './explore-vote-data';
import { findCandidate, candidateSelOf } from './explore-vote-candidates';
import { formatVotos, formatPercent, formatRank, countUpLabel } from './explore-vote-anim';
import { g26PartyColor } from './explore-g26-constants';

/** Nivel ativo do overlay: municipios do AM ou zonas de Manaus (close-up). */
export type VoteLevel = 'mun' | 'zone';

/** Snapshot que dirige o overlay (montado pelo ExploreMap a cada render). */
export interface VoteLayerState {
  readonly voteViz: ExploreVoteViz;
  readonly year: ElectoralYear;
  /** Id do candidato selecionado (null = agregado/vencedor de cada cidade). */
  readonly candidate: string | null;
  /** Nivel ativo (municipio ou zona), derivado do zoom pelo ExploreMap. */
  readonly level: VoteLevel;
  /**
   * Modo 2026: linhas OFICIAIS por municipio ja derivadas (deriveG26VoteRows). Quando
   * presentes substituem a distribuicao sintetica e a cor usa a paleta de 2026.
   */
  readonly g26Rows?: ReadonlyMap<string, VoteRow> | null;
}

/** Rotulo de uma bolha: texto final + (opcional) alvo de count-up e seu tipo. */
interface BubbleLabel {
  readonly text: string;
  readonly countup: 'votos' | 'percent' | null;
  readonly target: number;
}

/** Resolve a selecao reduzida a partir do id do candidato (null = agregado). */
function selFromCandidate(candidate: string | null): VoteCandidateSel | null {
  if (!candidate) return null;
  const cand = findCandidate(candidate);
  return cand ? candidateSelOf(cand) : null;
}

/** Texto/alvo do rotulo conforme o modo (absolutos→votos, percentual→%, ranking→colocacao). */
function labelOf(row: VoteRow, viz: ExploreVoteViz): BubbleLabel {
  if (viz === 'percentual') return { text: formatPercent(row.percentual), countup: 'percent', target: row.percentual };
  if (viz === 'ranking') return { text: formatRank(row.ranking), countup: null, target: row.ranking };
  return { text: formatVotos(row.votosAbsolutos), countup: 'votos', target: row.votosAbsolutos };
}

/** HTML do divIcon da bolha: var(--bub) carrega a cor; var(--i) escalona a entrada. */
function bubbleHtml(label: BubbleLabel, color: string, index: number): string {
  const data = label.countup ? ` data-countup="${label.countup}" data-target="${label.target}"` : '';
  return `<div class="exp-vote-bubble" style="--bub:${color};--i:${index}">`
    + `<span class="exp-vote-label"${data}>${label.text}</span></div>`;
}

/**
 * Camada do overlay de votos. Um unico L.layerGroup hospeda bolhas/calor; render()
 * limpa e repinta; clear() esvazia. Reusa os centroides recebidos do ExploreMap.
 */
export class ExploreVoteLayer {
  private map: LeafletMap | null = null;
  private munCentroids: ReadonlyMap<string, LatLng> = new Map();
  private zoneCentroids: ReadonlyMap<string, LatLng> = new Map();
  private group: LayerGroup | null = null;
  // Cor do partido da bolha/calor: paleta historica ou a de 2026 (definida no render).
  private colorOf: (party: string) => string = bubbleColorOf;

  /** Liga o mapa + os centroides (municipios e zonas) ja indexados pelo ExploreMap (DI). */
  bind(map: LeafletMap, munCentroids: ReadonlyMap<string, LatLng>, zoneCentroids: ReadonlyMap<string, LatLng>): void {
    this.map = map;
    this.munCentroids = munCentroids;
    this.zoneCentroids = zoneCentroids;
  }

  /** Garante (cria 1x) o layerGroup sobre o mapa. */
  private ensureGroup(): LayerGroup | null {
    if (!this.map) return null;
    if (!this.group) this.group = L.layerGroup().addTo(this.map);
    return this.group;
  }

  /** Repinta o overlay para o estado dado (ou limpa quando 'nenhum'). */
  render(state: VoteLayerState): void {
    this.clear();
    if (state.voteViz === 'nenhum') return;
    const group = this.ensureGroup();
    if (!group) return;
    const zone = state.level === 'zone' && !state.g26Rows;
    const rows = state.g26Rows ?? this.syntheticRows(state, zone);
    const centroids = zone ? this.zoneCentroids : this.munCentroids;
    this.colorOf = state.g26Rows ? g26PartyColor : bubbleColorOf;
    if (state.voteViz === 'calor') this.renderHeat(group, rows, centroids);
    else this.renderBubbles(group, rows, state.voteViz, centroids);
  }

  /** Linhas sinteticas (anos historicos) do nivel ativo. */
  private syntheticRows(state: VoteLayerState, zone: boolean): ReadonlyMap<string, VoteRow> {
    const sel = selFromCandidate(state.candidate);
    return zone ? deriveZoneVoteRows(state.year, sel) : deriveVoteRows(state.year, sel);
  }

  /** Esvazia o overlay (sem remover o group do mapa). */
  clear(): void {
    this.group?.clearLayers();
  }

  /** Desenha uma bolha por regiao (raio escalado em "absolutos"; fixo nos demais). */
  private renderBubbles(group: LayerGroup, rows: ReadonlyMap<string, VoteRow>, viz: ExploreVoteViz, centroids: ReadonlyMap<string, LatLng>): void {
    const scaleMax = voteScaleMax(rows.values());
    let index = 0;
    rows.forEach((row, key) => {
      const center = centroids.get(key);
      if (!center) return;
      const radius = viz === 'absolutos' ? raioDaBolha(row.votosAbsolutos, scaleMax) : BUBBLE_BASE_RADIUS;
      group.addLayer(this.bubbleMarker(center, row, viz, radius, index));
      index += 1;
    });
    this.animateLabels();
  }

  /** Marcador-bolha (divIcon HTML) centrado no centroide. */
  private bubbleMarker(center: LatLng, row: VoteRow, viz: ExploreVoteViz, radius: number, index: number) {
    const size = Math.round(radius * 2);
    const icon = L.divIcon({
      className: 'exp-vote-divicon',
      html: bubbleHtml(labelOf(row, viz), this.colorOf(row.party), index),
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
    return L.marker(center, { icon, interactive: false, keyboard: false });
  }

  /** Campo de calor: um circulo translucido por regiao (matiz do partido, peso→raio/opacidade). */
  private renderHeat(group: LayerGroup, rows: ReadonlyMap<string, VoteRow>, centroids: ReadonlyMap<string, LatLng>): void {
    const scaleMax = voteScaleMax(rows.values());
    rows.forEach((row, key) => {
      const center = centroids.get(key);
      if (!center) return;
      const weight = heatWeightOf(row.votosAbsolutos, scaleMax);
      group.addLayer(L.circleMarker(center, {
        radius: heatRadiusFor(weight),
        fillColor: heatColorFor(this.colorOf(row.party), weight),
        fillOpacity: heatOpacityFor(weight),
        stroke: false,
        interactive: false,
        // mix-blend-mode no CSS funde os circulos sobrepostos (campo de densidade).
        className: 'exp-heat-dot',
        pane: 'overlayPane',
      }));
    });
  }

  /** Dispara o count-up nos rotulos ja no DOM (enhancement; final ja visivel). */
  private animateLabels(): void {
    animateVoteLabels(document);
  }
}

/** Roda o count-up em cada rotulo com data-countup sob `root` (helper testavel). */
export function animateVoteLabels(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('.exp-vote-label[data-countup]').forEach((el) => {
    const target = Number(el.dataset.target);
    if (el.dataset.countup === 'votos') countUpLabel(el, target, formatVotos);
    else countUpLabel(el, target, (v) => formatPercent(v));
  });
}
